<script setup>
import { computed, ref } from 'vue';
import Spinner from 'shared/components/Spinner.vue';

const props = defineProps({
  data: {
    type: Array,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Distribuicao por Hora',
  },
  color: {
    type: String,
    default: '#3b82f6',
  },
});

const tooltip = ref({ show: false, x: 0, label: '', value: 0 });

// Chart dimensions
const chartWidth = 800;
const chartHeight = 200;
const padding = { top: 20, right: 20, bottom: 40, left: 40 };

// Maximum value for scaling
const maxValue = computed(() => {
  if (!props.data || props.data.length === 0) return 1;
  return Math.max(...props.data.map(d => d.count), 1);
});

// Bar width calculation
const barWidth = computed(() => {
  const availableWidth = chartWidth - padding.left - padding.right;
  return Math.max(availableWidth / 24 - 4, 10);
});

// Generate bars
const bars = computed(() => {
  if (!props.data || props.data.length === 0) return [];

  const availableWidth = chartWidth - padding.left - padding.right;
  const barSpacing = availableWidth / 24;

  return props.data.map((d, i) => {
    const height =
      (d.count / maxValue.value) * (chartHeight - padding.top - padding.bottom);
    return {
      x: padding.left + i * barSpacing + (barSpacing - barWidth.value) / 2,
      y: chartHeight - padding.bottom - height,
      width: barWidth.value,
      height: Math.max(height, 2),
      hour: d.hour,
      label: d.label,
      count: d.count,
    };
  });
});

// Y-axis ticks
const yTicks = computed(() => {
  const ticks = [];
  const step = Math.ceil(maxValue.value / 4);
  for (let i = 0; i <= maxValue.value; i += step) {
    ticks.push({
      value: i,
      y:
        chartHeight -
        padding.bottom -
        (i / maxValue.value) * (chartHeight - padding.top - padding.bottom),
    });
  }
  return ticks;
});

function showTooltip(event, bar) {
  tooltip.value = {
    show: true,
    x: bar.x + bar.width / 2,
    label: bar.label,
    value: bar.count,
  };
}

function hideTooltip() {
  tooltip.value.show = false;
}
</script>

<template>
  <div class="flex flex-col rounded-xl border border-n-weak bg-n-solid-2 p-4">
    <div class="flex items-center gap-2 mb-4">
      <span class="i-lucide-clock text-lg text-n-blue-10" />
      <h3 class="text-base font-semibold text-n-slate-12">{{ title }}</h3>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex items-center justify-center h-52">
      <Spinner size="large" />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="!data || data.length === 0"
      class="flex items-center justify-center h-52 text-n-slate-11"
    >
      <span class="text-sm">Sem dados disponiveis</span>
    </div>

    <!-- Chart -->
    <div v-else class="relative">
      <svg
        :viewBox="`0 0 ${chartWidth} ${chartHeight}`"
        class="w-full h-52"
        preserveAspectRatio="xMidYMid meet"
      >
        <!-- Grid lines -->
        <g class="grid-lines">
          <line
            v-for="tick in yTicks"
            :key="tick.value"
            :x1="padding.left"
            :y1="tick.y"
            :x2="chartWidth - padding.right"
            :y2="tick.y"
            stroke="currentColor"
            stroke-opacity="0.1"
            stroke-dasharray="4"
          />
        </g>

        <!-- Y-axis labels -->
        <g class="y-axis">
          <text
            v-for="tick in yTicks"
            :key="tick.value"
            :x="padding.left - 8"
            :y="tick.y + 4"
            text-anchor="end"
            class="fill-n-slate-10 text-xs"
          >
            {{ tick.value }}
          </text>
        </g>

        <!-- Bars -->
        <g class="bars">
          <rect
            v-for="bar in bars"
            :key="bar.hour"
            :x="bar.x"
            :y="bar.y"
            :width="bar.width"
            :height="bar.height"
            :fill="color"
            rx="3"
            class="cursor-pointer transition-opacity hover:opacity-80"
            @mouseenter="showTooltip($event, bar)"
            @mouseleave="hideTooltip"
          />
        </g>

        <!-- X-axis labels (every 3 hours) -->
        <g class="x-axis">
          <text
            v-for="bar in bars.filter((_, i) => i % 3 === 0)"
            :key="bar.hour"
            :x="bar.x + bar.width / 2"
            :y="chartHeight - 10"
            text-anchor="middle"
            class="fill-n-slate-10 text-xs"
          >
            {{ bar.label }}
          </text>
        </g>
      </svg>

      <!-- Tooltip -->
      <div
        v-if="tooltip.show"
        class="absolute pointer-events-none px-2 py-1 rounded-md bg-n-solid-3 border border-n-weak shadow-lg text-xs"
        :style="{
          left: `${(tooltip.x / chartWidth) * 100}%`,
          top: '10px',
          transform: 'translateX(-50%)',
        }"
      >
        <div class="font-medium text-n-slate-12">
          {{ tooltip.value }} conversas
        </div>
        <div class="text-n-slate-11">{{ tooltip.label }}</div>
      </div>
    </div>
  </div>
</template>
